import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import config from "../../config";
import { useGetFieldAdminProfileQuery, useSaveFieldAdminProfileMutation } from "./fieldAdminApi";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { BG, CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { BAD_RED_TEXT, teamLabel, initialsOf, stripErrorPrefix } from "./designTokens";
import { FATextField } from "./StockFormShell";
import { FAPageState } from "./FAStates";
import { FASectionLabel, FAPageShell, FAPageHeader } from "./FALayout";
import { FAPrimaryButton, FAGhostButton } from "./FAControls";
import DesignSwitch from "./DesignSwitch";
import { readLargerText, writeLargerText } from "./textScale";

/** The design's `.field-card` — padding sits on the CARD (4px 16px) and the rows carry only
 *  vertical padding, so each row's divider spans the card's inner width rather than running
 *  wall to wall. */
export const FIELD_CARD_SX = {
  backgroundColor: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: "12px",
  padding: "4px 16px",
};

/** The design's `.field-row` grid: `140px 1fr auto`, gap 16px, 13px vertical padding.
 *
 *  Under 720px it collapses to `1fr auto` with the label spanning both columns — the build
 *  previously pinned the label to a 130px flex column at every width, which squeezes the
 *  value on a phone, the one screen this app is actually used on. MUI's breakpoints don't
 *  land on 720, so this is a raw media query (same approach FAPageHeader already takes for
 *  the design's 1.4rem title).
 */
export const FIELD_ROW_SX = {
  display: "grid",
  gridTemplateColumns: "140px 1fr auto",
  gap: "16px",
  alignItems: "center",
  padding: "13px 0",
  borderBottom: "1px solid rgba(255,255,255,0.04)",
  "&:last-of-type": { borderBottom: "none" },
  "@media (max-width: 720px)": {
    gridTemplateColumns: "1fr auto",
    "& > .fa-field-label": { gridColumn: "1 / -1" },
  },
};

export const FIELD_LABEL_SX = { fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", fontWeight: 500 };
export const FIELD_VALUE_SX = { fontSize: "0.9rem", color: "#fff", fontWeight: 500, minWidth: 0 };

// The design locks every identity row with a "managed in IMS" chip — these are read-only
// by definition, not fields we haven't wired up. The chip is the word alone: the build
// carried a padlock glyph the design never draws.
function LockedRow({ label, value }) {
  return (
    <Box sx={FIELD_ROW_SX}>
      <Typography className="fa-field-label" sx={FIELD_LABEL_SX}>{label}</Typography>
      <Typography sx={FIELD_VALUE_SX}>{value || "—"}</Typography>
      <Box sx={{ fontSize: "0.66rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.4)", backgroundColor: "rgba(255,255,255,0.04)", padding: "3px 7px", borderRadius: "4px", flexShrink: 0 }}>
        Locked
      </Box>
    </Box>
  );
}

/** A Contact row at rest: the design's `.field-row` with an amber `.field-edit` in the slot
 *  the Identity card fills with a Locked chip. The two cards therefore line up column for
 *  column, which is the point of the design using one row shape for both. */
function ContactRowResting({ label, value, onEdit }) {
  return (
    <Box sx={FIELD_ROW_SX}>
      <Typography className="fa-field-label" sx={FIELD_LABEL_SX}>{label}</Typography>
      <Typography sx={value ? FIELD_VALUE_SX : { ...FIELD_VALUE_SX, color: "rgba(255,255,255,0.35)", fontWeight: 400 }}>
        {value || "Not set"}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={onEdit}
        sx={{
          background: "none", border: "none", padding: 0, cursor: "pointer", flexShrink: 0,
          fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 600, color: AMBER,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Edit
      </Box>
    </Box>
  );
}

/** The same row, open for editing.
 *
 *  Mounted only while editing and keyed off nothing else, so the draft can be seeded in
 *  useState's initialiser — the row is a fresh component each time it opens, which is why
 *  there is no effect here re-syncing props into state. Cancel unmounts it and the draft
 *  goes with it.
 *
 *  The label moves onto its own line because the emergency row carries two inputs; forcing
 *  those into the 1fr column of a 140px grid leaves each about 90px wide on a phone.
 */
function ContactRowEditor({ label, fields, initial, onCancel, onSave, isSaving, error }) {
  const [draft, setDraft] = useState(initial);

  const submit = () => onSave(draft);
  const onKeyDown = (e) => {
    // A row with one input behaves like a single-field form; Escape backs out of either.
    if (e.key === "Enter" && fields.length === 1) { e.preventDefault(); submit(); }
    if (e.key === "Escape") { e.preventDefault(); onCancel(); }
  };

  return (
    <Box sx={{ padding: "13px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", "&:last-of-type": { borderBottom: "none" } }}>
      <Typography sx={{ ...FIELD_LABEL_SX, mb: "8px" }}>{label}</Typography>
      <Box sx={{ display: "grid", gap: "10px" }}>
        {fields.map((field) => (
          <FATextField
            key={field.key}
            size="small"
            autoFocus={field === fields[0]}
            value={draft[field.key] ?? ""}
            placeholder={field.placeholder}
            onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
            onKeyDown={onKeyDown}
          />
        ))}
      </Box>
      {error ? <Typography sx={{ color: BAD_RED_TEXT, fontSize: "0.8rem", mt: "10px" }}>{error}</Typography> : null}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", mt: "12px" }}>
        <FAGhostButton onClick={onCancel} disabled={isSaving}>Cancel</FAGhostButton>
        <FAPrimaryButton onClick={submit} disabled={isSaving} sx={{ px: "18px" }}>
          {isSaving ? "Saving…" : "Save"}
        </FAPrimaryButton>
      </Box>
    </Box>
  );
}

/** The design's `.ims-callout` — a full indigo card at the foot of the page, not a line of
 *  small print. Indigo rather than amber because it leaves the app: every amber affordance
 *  on this page acts in place.
 *
 *  Rendered only when REACT_APP_IMS_API is set. envsubst substitutes an unset variable with
 *  "", so a missing config is falsy here rather than the literal "$REACT_APP_IMS_API" — but
 *  the unsubstituted form is guarded too, in case the template is served without the
 *  entrypoint having run. An empty href is worse than no link at all.
 *
 *  IMS mounts its router with a hardcoded basename of "ims" (its App.js), so every page
 *  lives under /ims/ — linking to the bare origin renders an empty shell, not a redirect.
 *  REACT_APP_IMS_API is the origin; the /ims segment is added here, and stripped first in
 *  case an environment sets the variable with it already included.
 */
function IMSCallout() {
  const rawImsUrl = config.REACT_APP_IMS_API || "";
  const imsOrigin = rawImsUrl.startsWith("$") ? "" : rawImsUrl.replace(/\/+$/, "").replace(/\/ims$/, "");
  if (!imsOrigin) return null;

  return (
    <Box
      component="a"
      href={`${imsOrigin}/ims/teams`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "grid", gridTemplateColumns: "38px 1fr auto", gap: "14px", alignItems: "center",
        backgroundColor: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)",
        borderRadius: "12px", padding: "14px 16px", mt: "16px",
        textDecoration: "none", color: "inherit",
        "&:hover": { borderColor: "rgba(99,102,241,0.45)" },
      }}
    >
      <Box sx={{ width: "38px", height: "38px", borderRadius: "9px", backgroundColor: "rgba(99,102,241,0.18)", color: "#A5B4FC", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <BusinessOutlinedIcon sx={{ fontSize: "1.1rem" }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#fff" }}>Manage role &amp; team in IMS</Typography>
        <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.55)", mt: "2px" }}>Onboarding, reassignment, company link</Typography>
      </Box>
      <OpenInNewIcon sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.4)", flexShrink: 0 }} />
    </Box>
  );
}

export default function Profile() {
  const dispatch = useDispatch();
  const { data, isSuccess, isError, error, refetch } = useGetFieldAdminProfileQuery();
  const [saveProfile, { isLoading: isSaving }] = useSaveFieldAdminProfileMutation();

  // Which row is open, if any — the design edits one field at a time. The saved values are
  // read straight from the query rather than mirrored into state: the mutation invalidates
  // `fa_profile`, so the row re-renders from the server's own answer once the save lands.
  // The old copy-on-hydrate needed a guard to stop a background refetch clobbering typing;
  // there is nothing to clobber now, because only the open editor holds a draft.
  const [editing, setEditing] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // Not part of the profile payload — see textScale.js for why this one preference is
  // device-local. writeLargerText notifies FieldAdminBackground, which owns the root scale.
  const [largerText, setLargerTextState] = useState(readLargerText);
  const setLargerText = (enabled) => { setLargerTextState(enabled); writeLargerText(enabled); };

  if (!isSuccess) return <FAPageState isError={isError} error={error} onRetry={refetch} count={4} />;

  const identity = data.identity;
  // The design's three-part "B-4 · Soweto · Zone N3". Zone is blank on teams whose area
  // name already encodes it ("Soweto Zone N3"), so it is joined on truthiness rather than
  // rendered as an empty segment.
  const teamAssignment = [teamLabel(identity.team_name, identity.team_id), identity.area_name, identity.zone]
    .filter(Boolean).join(" · ");
  const contact = data.contact;
  const openRow = (key) => { setSaveError(null); setEditing(key); };
  const closeRow = () => { setSaveError(null); setEditing(null); };

  // Only the row's own keys are sent. The endpoint is already partial=True, so an untouched
  // field is left alone rather than round-tripped back at whatever the page last read.
  const handleSave = async (patch) => {
    setSaveError(null);
    const trimmed = Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, (v || "").trim()]));
    const result = await saveProfile(trimmed);
    if (result.error) {
      setSaveError(stripErrorPrefix(result.error?.data?.error_message) || "Could not save. Try again.");
      return;
    }
    setEditing(null);
    dispatch(showSuccess("Contact details saved"));
  };

  return (
      <FAPageShell>
        <FAPageHeader title="Profile" sub="Identity is managed in IMS · personal preferences live here." />

        {/* Hero */}
        {/* .hero-card — an amber-tinted gradient panel, not the neutral card the rest of the
            page uses. It is the one element on the page that is about the person. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "18px", background: "linear-gradient(135deg, rgba(253,185,64,0.08), rgba(253,185,64,0.02))", border: "1px solid rgba(253,185,64,0.2)", borderRadius: "14px", p: "18px 22px", mb: "24px", flexWrap: "wrap" }}>
          <Box sx={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg,#FDB940,#E0A020)", color: BG, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.03em", flexShrink: 0 }}>
            {initialsOf(identity.name)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}>{identity.name || "—"}</Typography>
            <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.7)", mt: "2px" }}>
              {identity.role}
              {identity.team_name ? (
                <>
                  {" · "}
                  <Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{teamLabel(identity.team_name, identity.team_id)}</Box>
                </>
              ) : null}
            </Typography>
          </Box>
        </Box>

        <FASectionLabel>Identity · managed in IMS</FASectionLabel>
        <Box sx={{ ...FIELD_CARD_SX, mb: "22px" }}>
          <LockedRow label="Full name" value={identity.name} />
          <LockedRow label="Role" value={identity.role} />
          {/* Not in the design, which puts Email under Contact as editable. Ours is the
              Userport sign-in credential — changing it changes how you log in, which is not
              a contact edit — so it stays here, locked. */}
          <LockedRow label="Sign-in email" value={identity.email} />
          <LockedRow label="Installer company" value={identity.company_name} />
          <LockedRow label="Team assignment" value={teamAssignment} />
        </Box>

        <FASectionLabel>Contact</FASectionLabel>
        <Box sx={{ ...FIELD_CARD_SX, mb: "10px" }}>
          {editing === "mobile" ? (
            <ContactRowEditor
              label="Mobile"
              fields={[{ key: "mobile", placeholder: "e.g. 082 555 0142" }]}
              initial={{ mobile: contact.mobile || "" }}
              onCancel={closeRow} onSave={handleSave} isSaving={isSaving} error={saveError}
            />
          ) : (
            <ContactRowResting label="Mobile" value={contact.mobile} onEdit={() => openRow("mobile")} />
          )}

          {/* Two stored fields, one row — the design shows them joined as
              "Nomsa Ndlovu · +27 84 332 7711", so they are edited together. */}
          {editing === "emergency" ? (
            <ContactRowEditor
              label="Emergency contact"
              fields={[
                { key: "emergency_contact_name", placeholder: "Name" },
                { key: "emergency_contact_number", placeholder: "e.g. +27 84 332 7711" },
              ]}
              initial={{
                emergency_contact_name: contact.emergency_contact_name || "",
                emergency_contact_number: contact.emergency_contact_number || "",
              }}
              onCancel={closeRow} onSave={handleSave} isSaving={isSaving} error={saveError}
            />
          ) : (
            <ContactRowResting
              label="Emergency contact"
              value={[contact.emergency_contact_name, contact.emergency_contact_number].filter(Boolean).join(" · ")}
              onEdit={() => openRow("emergency")}
            />
          )}
        </Box>

        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", mb: "22px", lineHeight: 1.5 }}>
          Contact details are held in this app for now. Where an edit should land for HR or IMS review is an open
          question in the design, so nothing is written back to your IMS person record.
        </Typography>

        <FASectionLabel>Preferences</FASectionLabel>
        <Box sx={FIELD_CARD_SX}>
          {/* .toggle-row — 1fr auto, same 13px rhythm as .field-row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "16px", alignItems: "center", padding: "13px 0" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="label" htmlFor="pref-larger-text" sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#fff", cursor: "pointer" }}>
                Larger text
              </Typography>
              <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", mt: "2px" }}>
                For in-vehicle / sunlight readability
              </Typography>
            </Box>
            <DesignSwitch id="pref-larger-text" checked={largerText} onChange={setLargerText} />
          </Box>
        </Box>

        {/* The design's other three toggles are absent rather than shown disabled: each is a
            promise that something will be SENT, and there is no push service, no SMS gateway
            and no email backend to send it (the same gap that blocks "send to email" on the
            H&S export). A switch that stored a choice and delivered nothing would be worse
            than none. "Larger text" is the one that needs no channel, so it is real. */}
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", mt: "10px", lineHeight: 1.5 }}>
          Push notifications, SMS fallback and the daily summary email need a delivery channel
          this app does not have yet, so they are not shown.
        </Typography>

        <IMSCallout />
      </FAPageShell>
  );
}
